import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  StyleSheet,
  Dimensions,
  ScrollView,
  ActivityIndicator,
  Text,
  Pressable,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { createCheckoutSession } from '../services/payments-service';
import { isSuccessful } from '../services/api-requests';
import type { RootStackParamList } from '../navigation/stackTypes';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useFocusEffect } from '@react-navigation/native';
import { notificationService } from '../services/notification-service';
import { loadStripe } from '@stripe/stripe-js';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from '@stripe/react-stripe-js';
import { verifyStripeSubscription } from '../services/stripe-service';

const { width, height } = Dimensions.get('window');

const FOOTER_HEIGHT = height * 0.07; // adjust to your actual footer height

type SetupBillingNavigationProps = NativeStackScreenProps<
  RootStackParamList,
  'SetupBilling'
>;

export type SetupBillingParams = {
  returnToOnboarding?: boolean;
  /** Called with `true` if the checkout succeeded, `false` otherwise */
  onSubmit?: (success: boolean) => void;
};

const SetupBilling: React.FC<SetupBillingNavigationProps> = ({
  navigation,
  route,
}) => {
  const { returnToOnboarding, onSubmit } = route.params;
  const [clientSessionSecret, setClientSessionSecret] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checkoutKey, setCheckoutKey] = useState(0); // Force re-mount when needed
  const [shouldRenderCheckout, setShouldRenderCheckout] = useState(false);
  const stripeAreaHeight = height - FOOTER_HEIGHT;
  const isMountedRef = useRef(true);

  useFocusEffect(
    useCallback(() => {
      let isActive = true;
      
      const fetchCheckoutSession = async () => {
        if (!isActive) return;
        
        // First, hide the checkout component to ensure cleanup
        setShouldRenderCheckout(false);
        setIsLoading(true);
        setError(null);
        
        // Wait a bit for the previous checkout to unmount
        await new Promise(resolve => setTimeout(resolve, 100));
        
        try {
          const response = await createCheckoutSession();
          
          if (isActive && isSuccessful(response.status) && response.payload?.clientSecret) {
            setClientSessionSecret(response.payload.clientSecret);
            setCheckoutKey(prev => prev + 1); // Force re-mount of checkout component
            // Wait a bit more before showing the new checkout
            setTimeout(() => {
              if (isActive) {
                setShouldRenderCheckout(true);
              }
            }, 200);
          } else if (isActive) {
            const errorMessage = response.payload?.message ?? 'Failed to create checkout session';
            setError(errorMessage);
            notificationService.sendNotification('error', errorMessage);
            navigation.navigate('OnboardingStatus' as any);
          }
        } catch (error) {
          if (isActive) {
            const errorMessage = `Error initializing checkout: ${error}`;
            setError(errorMessage);
            notificationService.sendNotification('error', errorMessage);
          }
        } finally {
          if (isActive) {
            setIsLoading(false);
          }
        }
      };

      fetchCheckoutSession();

      // Cleanup function
      return () => {
        isActive = false;
        setShouldRenderCheckout(false);
      };
    }, [navigation])
  );

  // Monitor clientSessionSecret changes
  useEffect(() => {
    // Debug logging for client session secret changes
  }, [clientSessionSecret]);
  
  if (isLoading) {
    return (
      <View style={styles.container}>
        <SafeAreaView style={styles.safeArea}>
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#01556F" />
            <Text style={styles.loadingText}>Setting up payment...</Text>
          </View>
        </SafeAreaView>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.container}>
        <SafeAreaView style={styles.safeArea}>
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>Error: {error}</Text>
            <Pressable
              style={styles.retryButton}
              onPress={async () => {
                setError(null);
                setIsLoading(true);
                setClientSessionSecret('');
                setShouldRenderCheckout(false);
                
                // Wait for cleanup
                await new Promise(resolve => setTimeout(resolve, 100));
                
                try {
                  const response = await createCheckoutSession();
                  if (isSuccessful(response.status) && response.payload?.clientSecret) {
                    setClientSessionSecret(response.payload.clientSecret);
                    setCheckoutKey(prev => prev + 1);
                    setTimeout(() => {
                      setShouldRenderCheckout(true);
                    }, 200);
                  } else {
                    setError(response.payload?.message ?? 'Failed to create checkout session');
                  }
                } catch (error) {
                  setError(`Error initializing checkout: ${error}`);
                } finally {
                  setIsLoading(false);
                }
              }}
            >
              <Text style={styles.retryButtonText}>Retry</Text>
            </Pressable>
          </View>
        </SafeAreaView>
      </View>
    );
  }

  if (!clientSessionSecret) {
    return (
      <View style={styles.container}>
        <SafeAreaView style={styles.safeArea}>
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>No checkout session available</Text>
          </View>
        </SafeAreaView>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <SafeAreaView style={styles.safeArea}>
        <ScrollView
          style={[styles.stripeContainer, { height: stripeAreaHeight }]}
          contentContainerStyle={styles.scrollContent}
        >
          {shouldRenderCheckout && (
            <EmbeddedCheckoutProvider
              key={checkoutKey} // Force re-mount when key changes
              stripe={loadStripe(process.env.STRIPE_PUBLISHABLE_KEY!)}
              options={{
                clientSecret: clientSessionSecret,
                onComplete: async () => {
                  const success = await verifyStripeSubscription();
                  //catch the onSubmit if it was included
                  if (success) {
                    if (onSubmit) {
                      onSubmit(isSuccessful(success.status) ?? false);
                      return;
                    }
                  }

                  //fall back to regular navigation logic:
                  if (success) {
                    if (returnToOnboarding) {
                      navigation.navigate('OnboardingStatus' as any);
                    } else {
                      navigation.navigate('MyChargers' as any);
                    }
                  } else {
                    notificationService.sendNotification(
                      'error',
                      'Payment failed.'
                    );
                  }
                },
              }}
            >
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          )}
        </ScrollView>
      </SafeAreaView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0B1E1D',
  },
  safeArea: {
    flex: 1,
  },
  stripeContainer: {
    width: '100%',
    overflow: 'hidden', // hide any overflow within the stripe area
  },
  scrollContent: {
    flexGrow: 1, // let content fill the scroll area
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
  },
  content: {
    flex: 1,
    padding: 16,
    justifyContent: 'space-between',
  },
  cardContainer: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
    marginBottom: 24,
  },
  titleText: {
    color: '#01556F',
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  priceText: {
    color: '#01556F',
    fontSize: 20,
    marginBottom: 16,
  },
  descriptionText: {
    color: '#01556F',
    fontSize: 16,
    lineHeight: 24,
  },
  successText: {
    color: '#01556F',
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 16,
  },
  messageText: {
    color: '#01556F',
    fontSize: 16,
    lineHeight: 24,
  },
  submitButton: {
    backgroundColor: '#01556F',
    borderRadius: 8,
    padding: 16,
    alignItems: 'center',
    marginBottom: 16,
  },
  buttonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: 'white',
    textAlign: 'center',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  errorText: {
    fontSize: 16,
    color: 'white',
    textAlign: 'center',
    marginBottom: 16,
  },
  retryButton: {
    backgroundColor: '#01556F',
    borderRadius: 8,
    padding: 16,
    alignItems: 'center',
  },
  retryButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
});

export default SetupBilling;
